/**
 * NativePushService - FCM token registration service for Hotwire Native apps
 *
 * Shared service module for native push notification management.
 * Used by: native_push_controller.js, first_visit_prompt_controller.js
 *
 * @module native_push_service
 */
import { NativeBridge } from "./native_bridge"

// API endpoint (SSOT)
const PUSH_TOKENS_ENDPOINT = "/api/native/push_tokens"

// Request timeout in milliseconds (10 seconds)
const REQUEST_TIMEOUT_MS = 10000

/**
 * Get CSRF token from meta tag
 * @returns {string}
 */
export function getCsrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || ""
}

/**
 * Get OS version from user agent
 * @returns {string} e.g., "Android 14", "iOS 17.2", "Unknown"
 */
export function getOsVersion() {
  const ua = navigator.userAgent
  const androidMatch = ua.match(/Android\s([\d.]+)/)
  const iosMatch = ua.match(/OS\s([\d_]+)/)

  if (androidMatch) return `Android ${androidMatch[1]}`
  if (iosMatch) return `iOS ${iosMatch[1].replace(/_/g, ".")}`
  return "Unknown"
}

/**
 * Get platform identifier
 * @returns {string} "android" | "ios"
 */
export function getPlatform() {
  return NativeBridge.isAndroid() ? "android" : "ios"
}

/**
 * Register FCM token with Rails backend
 *
 * @param {string} token - FCM device token
 * @param {Object} options - Optional configuration
 * @param {string} options.endpoint - Custom API endpoint
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function registerToken(token, options = {}) {
  const endpoint = options.endpoint || PUSH_TOKENS_ENDPOINT

  // Create AbortController for request timeout
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken()
      },
      body: JSON.stringify({
        fcm_token: token,
        platform: getPlatform(),
        device_model: navigator.userAgent,
        os_version: getOsVersion()
      }),
      signal: controller.signal
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      const data = await response.json()
      return { success: false, error: data.error || "Registration failed", code: data.code }
    }

    const data = await response.json()
    return { success: true, data }
  } catch (error) {
    clearTimeout(timeoutId)

    // Handle AbortError (timeout) specifically
    if (error.name === "AbortError") {
      console.error("[NativePushService] Registration timeout after", REQUEST_TIMEOUT_MS, "ms")
      return { success: false, error: "Request timeout", code: "TIMEOUT" }
    }

    console.error("[NativePushService] Registration error:", error)
    return { success: false, error: error.message, code: "NETWORK_ERROR" }
  }
}

/**
 * Unregister FCM token from Rails backend
 *
 * @param {Object} options - Optional configuration
 * @param {string} options.endpoint - Custom API endpoint
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function unregisterToken(options = {}) {
  const endpoint = options.endpoint || PUSH_TOKENS_ENDPOINT

  // Create AbortController for request timeout
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  try {
    const response = await fetch(endpoint, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken()
      },
      body: JSON.stringify({ platform: getPlatform() }),
      signal: controller.signal
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      const data = await response.json()
      return { success: false, error: data.error || "Unregistration failed", code: data.code }
    }

    return { success: true }
  } catch (error) {
    clearTimeout(timeoutId)

    if (error.name === "AbortError") {
      console.error("[NativePushService] Unregister timeout after", REQUEST_TIMEOUT_MS, "ms")
      return { success: false, error: "Request timeout", code: "TIMEOUT" }
    }

    console.error("[NativePushService] Unregister error:", error)
    return { success: false, error: error.message, code: "NETWORK_ERROR" }
  }
}

/**
 * Check if token is registered on server
 *
 * @param {Object} options - Optional configuration
 * @param {string} options.endpoint - Custom API endpoint
 * @returns {Promise<{registered: boolean, error: string|null}>}
 */
export async function checkRegistration(options = {}) {
  const endpoint = options.endpoint || PUSH_TOKENS_ENDPOINT

  // Create AbortController for request timeout
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  try {
    const response = await fetch(`${endpoint}?platform=${getPlatform()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken()
      },
      signal: controller.signal
    })

    clearTimeout(timeoutId)

    if (response.ok) {
      const data = await response.json()
      return {
        registered: data.active === true,
        error: null
      }
    } else if (response.status === 404) {
      // 404 = Not registered (normal case)
      return {
        registered: false,
        error: null
      }
    } else {
      // Server error
      const data = await response.json().catch(() => ({}))
      return {
        registered: false,
        error: data.error || `HTTP ${response.status}`
      }
    }
  } catch (error) {
    clearTimeout(timeoutId)

    if (error.name === "AbortError") {
      console.error("[NativePushService] checkRegistration timeout after", REQUEST_TIMEOUT_MS, "ms")
      return {
        registered: false,
        error: "Request timeout"
      }
    }

    console.error("[NativePushService] checkRegistration error:", error)
    return {
      registered: false,
      error: error.message || "Network error"
    }
  }
}

/**
 * Request permission and register token in one call
 *
 * @returns {Promise<{granted: boolean, token?: string, error?: string}>}
 */
export async function requestPermissionAndRegister() {
  if (!NativeBridge.isNative()) {
    return { granted: false, error: "Not running in native app" }
  }

  try {
    const result = await NativeBridge.Push.requestPermission()

    if (result.granted && result.token) {
      const registration = await registerToken(result.token)
      if (registration.success) {
        return { granted: true, token: result.token }
      }
      return { granted: true, token: result.token, error: registration.error }
    }

    return { granted: false }
  } catch (error) {
    console.error("[NativePushService] Permission error:", error)
    return { granted: false, error: error.message }
  }
}
